/**
 * Configuración para la conexión con el API backend
 */

// URL base del API
// Usa la variable de entorno o un valor predeterminado
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
export const API_ROOT_URL =
  process.env.REACT_APP_API_ROOT_URL
  || (API_BASE_URL.endsWith('/api') ? API_BASE_URL.slice(0, -4) : API_BASE_URL);

// Endpoints principales
export const API_ENDPOINTS = {
  // Autenticación
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/registro',
    PROFILE: '/auth/perfil',
  },
  
  // Doctor
  DOCTOR: {
    PROFILE: '/doctores/perfil',
    UPDATE_PROFILE: '/doctores/perfil',
    CONFIG_RECIPE: '/doctores/configuracion-receta',
    PDF_TEMPLATE: '/doctores/plantilla-receta-pdf',
    CONFIG_SCHEDULE: '/doctores/configuracion-horario',
    ADD_EXCEPTION: '/doctores/excepcion-horario',
    DELETE_EXCEPTION: (id) => `/doctores/excepcion-horario/${id}`,
    AVAILABLE: '/doctores/disponibles',
  },
  
  // Pacientes
  PATIENT: {
    LIST: '/pacientes',
    SIN_HISTORIA: '/pacientes/sin-historia',
    SEARCH_SRI_BY_CEDULA: (cedula) => `/pacientes/sri/cedula/${cedula}`,
    CREATE: '/pacientes',
    DETAIL: (id) => `/pacientes/${id}`,
    UPDATE: (id) => `/pacientes/${id}`,
  },
  
  // Citas
  APPOINTMENT: {
    LIST: '/citas',
    TODAY: '/citas/hoy',
    CREATE: '/citas',
    DETAIL: (id) => `/citas/${id}`,
    UPDATE_STATUS: (id) => `/citas/${id}/estado`,
    UPDATE_PAYMENT: (id) => `/citas/${id}/pago`,
    CHECK_AVAILABILITY: '/citas/disponibilidad/verificar',
    WEEKLY_AVAILABILITY: '/citas/disponibilidad/semanal',
    CREATE_WEB: '/citas/web',
  },
  
  // Plantillas
  TEMPLATE: {
    LIST: '/plantillas',
    CREATE: '/plantillas',
    DETAIL: (id) => `/plantillas/${id}`,
    UPDATE: (id) => `/plantillas/${id}`,
    DELETE: (id) => `/plantillas/${id}`,
    CREATE_OPHTHALMOLOGIC: '/plantillas/oftalmologica',
  },
  
  // Historias Clínicas
  MEDICAL_RECORD: {
    LIST_BY_PATIENT: (patientId) => `/historias-clinicas/paciente/${patientId}`,
    LIST_BY_CEDULA: (cedula) => `/historias-clinicas/paciente/cedula/${cedula}`,
    LIST: '/historias-clinicas',  // Agregando endpoint para listar todas las historias
    CREATE: '/historias-clinicas',
    DETAIL: (id) => `/historias-clinicas/${id}`,
    UPDATE: (id) => `/historias-clinicas/${id}`,
    ADD_DIAGNOSIS: (id) => `/historias-clinicas/${id}/diagnostico`,
    CREATE_RECIPE: (id, diagnosisId) => 
      `/historias-clinicas/${id}/diagnostico/${diagnosisId}/receta`,
  },
  
  // Servicios
  SERVICE: {
    LIST: '/servicios',
    CREATE: '/servicios',
    DETAIL: (id) => `/servicios/${id}`,
    UPDATE: (id) => `/servicios/${id}`,
    CHANGE_STATUS: (id) => `/servicios/${id}/estado`,
    PUBLIC_LIST: (doctorId) => `/servicios/publico/${doctorId}`,
  },
};

/**
 * Función para agregar headers de autenticación
 * @param {Object} headers - Headers actuales
 * @returns {Object} - Headers con token incluido
 */
export const authHeader = (headers = {}) => {
  const token = localStorage.getItem('token');
  
  if (token) {
    return {
      ...headers,
      'Authorization': `Bearer ${token}`,
    };
  }
  
  return headers;
};

/**
 * Servicio para manejar peticiones al API
 */
export const apiService = {
  /**
   * Realizar petición GET
   * @param {string} endpoint - URL del endpoint
   * @param {Object} params - Parámetros de la petición
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  get: async (endpoint, params = {}, auth = true) => {
    const url = new URL(`${API_BASE_URL}${endpoint}`, window.location.origin);
    
    // Agregar parámetros a la URL
    Object.keys(params).forEach(key => {
      url.searchParams.append(key, params[key]);
    });
    
    const headers = auth ? authHeader() : {};
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    return handleResponse(response);
  },
  
  /**
   * Realizar petición POST
   * @param {string} endpoint - URL del endpoint
   * @param {Object} data - Datos a enviar
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  post: async (endpoint, data = {}, auth = true) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(auth ? authHeader() : {}),
    };
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    
    return handleResponse(response);
  },
  
  /**
   * Realizar petición PUT
   * @param {string} endpoint - URL del endpoint
   * @param {Object} data - Datos a enviar
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  put: async (endpoint, data = {}, auth = true) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(auth ? authHeader() : {}),
    };
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data),
    });
    
    return handleResponse(response);
  },
  
  /**
   * Realizar petición PATCH
   * @param {string} endpoint - URL del endpoint
   * @param {Object} data - Datos a enviar
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  patch: async (endpoint, data = {}, auth = true) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(auth ? authHeader() : {}),
    };
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(data),
    });
    
    return handleResponse(response);
  },
  
  /**
   * Realizar petición DELETE
   * @param {string} endpoint - URL del endpoint
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  delete: async (endpoint, auth = true) => {
    const headers = auth ? authHeader() : {};
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'DELETE',
      headers,
    });
    
    return handleResponse(response);
  },
  
  /**
   * Subir archivo con formulario
   * @param {string} endpoint - URL del endpoint
   * @param {FormData} formData - Datos del formulario
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  upload: async (endpoint, formData, auth = true) => {
    const headers = auth ? authHeader() : {};
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: formData,
    });
    
    return handleResponse(response);
  },
  
  /**
   * Actualizar con archivo
   * @param {string} endpoint - URL del endpoint
   * @param {FormData} formData - Datos del formulario
   * @param {boolean} auth - Indica si requiere autenticación
   * @returns {Promise} - Promesa con la respuesta
   */
  updateWithFile: async (endpoint, formData, auth = true, method = 'PUT') => {
    const headers = auth ? authHeader() : {};
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers,
      body: formData,
    });
    
    return handleResponse(response);
  },
};

/**
 * Manejar respuesta del API
 * @param {Response} response - Respuesta fetch
 * @returns {Promise} - Promesa con datos o error
 */
async function handleResponse(response) {
  const data = await response.json();
  
  // Si la respuesta no es exitosa, lanzar error
  if (!response.ok) {
    // Si es error de autenticación, limpiar token
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    
    throw {
      status: response.status,
      ...data,
    };
  }
  
  return data;
}